// @ts-nocheck
// Redesigned voice-agent builder: Finder-style tree + detail + inspector.
// Same API contract as AgentBuilderPage, plus laya_config and llm_config.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, FileJson, ListTree, Loader2, Save } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { FillPage } from '@/components/layout/PageContainer';
import { PageLoader } from '@/components/feedback/PageLoader';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { AGENT_TYPE_LABELS } from '@/utilities/aiAgentsStore';
import { getUser } from '@/utilities/setting';
import { saveBot, saveDraft, getDraft, getAgent } from '@/service/aiAgentApi';
import { cn } from '@/lib/utils/cn';

import { FlattenSectionsContext } from '../builder-components/CollapsibleSection';
import KnowledgeBaseCard from '../builder-components/KnowledgeBaseCard';
import InstructionsCard from '../builder-components/InstructionsCard';
import FunctionsCard from '../builder-components/FunctionsCard';
import FlowchartCard from '../builder-components/FlowchartCard';
import FollowUpRulesSection from '../builder-components/FollowUpRulesSection';
import DataVariablesSection, {
  buildDataVariablesGroups,
  normalizeVariableType,
  normalizeVariableSource,
} from '../builder-components/DataVariablesSection';
import VoiceIdSection from '../builder-components/VoiceIdSection';
import LanguagesSection from '../builder-components/LanguagesSection';
import PersonaTypeSection from '../builder-components/PersonaTypeSection';
import ConversationFlowSection from '../builder-components/ConversationFlowSection';
import McpSection from '../builder-components/McpSection';
import HumanHandoverSection from '../builder-components/HumanHandoverSection';
import CompletionRulesSection from '../builder-components/CompletionRulesSection';

import BuilderTree from './BuilderTree';
import AgentSummary from './AgentSummary';
import LayaSection from './LayaSection';
import ImportJsonDialog from './ImportJsonDialog';
import HeadingInfo from '../builder-components/HeadingInfo';
import { flatten, rollUp } from './treeState';
import { validateLaya } from './layaLlm';
import {
  hydrateAgent,
  buildSavePayload as buildSavePayloadFor,
  buildFlowPayload as buildFlowPayloadFor,
  missingForPublish,
} from './agentPayload';

// Which sections block publishing, and a short note for the ones that do not.
const NEED = {
  Persona: { required: true },
  Voice: { required: true },
  Instructions: { required: true },
  'Knowledge Base': { required: true },
  'Completion Rules': { required: true },
  'Conversation Flow': { required: true },
  Languages: { note: 'Skip it and the agent speaks English (India).' },
  Functions: { note: 'Only needed if the agent must book or look things up through your API.' },
  'Fast answers': { note: 'Leave it off and every question goes to the AI.' },
  'Human Handover': { note: 'Off by default. Turn it on to let callers reach a live person.' },
  'Follow-up Rules': { note: 'Without it the agent stays quiet when the caller goes silent.' },
  'Data Variables': { note: 'Only needed to capture values such as a name or phone number.' },
  MCP: { note: 'Only needed if the AI must use outside tools.' },
};

function Detail({ crumbs, title, help, info, requiredNow, children }) {
  const need = NEED[title];
  const isRequired = requiredNow ?? need?.required;
  return (
    <div className="mx-auto w-full max-w-3xl">
      <p className="text-xs text-muted-foreground">{crumbs.join(' › ')}</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          {isRequired && <span aria-hidden className="mr-1 text-destructive">*</span>}
          {title}
        </h2>
        {info && <HeadingInfo text={info} />}
        {isRequired && (
          <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
            <span aria-hidden className="font-bold">!</span> Required
          </span>
        )}
        {need && !isRequired && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">Optional</span>
        )}
      </div>
      {help && <p className="mt-1 max-w-prose text-sm leading-relaxed text-muted-foreground">{help}</p>}
      {need?.note && <p className="mt-1 max-w-prose text-xs italic text-muted-foreground">Optional: {need.note}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

const AgentBuilderEditor = ({ loadedAgent }: any) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const agentId = searchParams.get('id') || loadedAgent?.agent_id || '';
  const agentType = searchParams.get('type');
  const agentTypeLabel = AGENT_TYPE_LABELS[agentType] || 'Voice Agent';

  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;

  const initialState = useRef(
    hydrateAgent(loadedAgent, {
      agentType,
      agentName: searchParams.get('name') || undefined,
      user,
      normalizers: { normalizeType: normalizeVariableType, normalizeSource: normalizeVariableSource },
    }),
  ).current;

  const [agentName, setAgentName] = useState(initialState.agentName);
  const [draftId, setDraftId] = useState(searchParams.get('draftId') || loadedAgent?.id || null);
  const draftIdRef = useRef(draftId);
  const [status, setStatus] = useState(loadedAgent?.status || 'draft');
  const [viewOnly, setViewOnly] = useState(isSuperAdmin || status === 'published');
  const [saving, setSaving] = useState(false);

  const [selectedKbId, setSelectedKbId] = useState(initialState.selectedKbId);
  const [instructions, setInstructions] = useState(initialState.instructions);
  const [functions, setFunctions] = useState(initialState.functions);
  const [followUp, setFollowUp] = useState(initialState.followUp);
  const [dataVariables, setDataVariables] = useState(initialState.dataVariables);
  const [voiceId, setVoiceId] = useState(initialState.voiceId);
  const [language, setLanguage] = useState(initialState.language);
  const [languageId, setLanguageId] = useState(initialState.languageId);
  const [personaType, setPersonaType] = useState(initialState.personaType);
  const [personaData, setPersonaData] = useState(initialState.personaData);
  const [conversationFlow, setConversationFlow] = useState(initialState.conversationFlow);
  const [mcpServers, setMcpServers] = useState(initialState.mcpServers);
  const [humanHandoverEnabled, setHumanHandoverEnabled] = useState(initialState.humanHandoverEnabled);
  const [completionRules, setCompletionRules] = useState(initialState.completionRules);
  // new jsonb columns — null for every agent saved before this feature
  const [laya, setLaya] = useState(initialState.laya);
  const [llm, setLlm] = useState(initialState.llm);
  const layaGloballyDisabled = loadedAgent?.laya_globally_enabled === false;

  const [flowGenerated, setFlowGenerated] = useState(Boolean(loadedAgent?.conversational_flow));
  const [lastUpdatedAt, setLastUpdatedAt] = useState(loadedAgent?.updated_at || new Date().toISOString());
  const lastUpdatedLabel = new Date(lastUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const [selectedId, setSelectedId] = useState('persona');
  const [expanded, setExpanded] = useState({ basics: true, knows: true, laya: true, ending: true });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  /** One JSON import fans out into every field; all of it stays editable. */
  const applyImport = ({ state: next, warnings }) => {
    setAgentName(next.agentName);
    setSelectedKbId(next.selectedKbId);
    setInstructions(next.instructions);
    setFunctions(next.functions);
    setFollowUp(next.followUp);
    setDataVariables(next.dataVariables);
    setVoiceId(next.voiceId);
    setLanguage(next.language);
    setLanguageId(next.languageId);
    setPersonaType(next.personaType);
    setPersonaData(next.personaData);
    setConversationFlow(next.conversationFlow);
    setMcpServers(next.mcpServers);
    setHumanHandoverEnabled(next.humanHandoverEnabled);
    setCompletionRules(next.completionRules);
    setLaya(next.laya);
    setLlm(next.llm);
    setFlowGenerated(Boolean(next.conversationFlow));
    setViewOnly(false);
    toast.success('Form filled from JSON — check it over before publishing.');
    warnings.forEach((w) => toast.warning(w));
  };

  const readOnly = viewOnly;
  // importing JSON only makes sense while creating: an existing agent would be overwritten
  const canImport = !agentId && !readOnly && !isSuperAdmin;
  const layaCheck = validateLaya(laya);

  // ------------------------------------------------- payloads (v1 contract)
  // One source of truth, tested in agentPayload.selfcheck.mjs.
  const formState = {
    agentId,
    agentType,
    agentName,
    draftId: draftIdRef.current || draftId,
    user,
    selectedKbId,
    instructions,
    functions,
    followUp,
    dataVariables,
    voiceId,
    language,
    languageId,
    personaType,
    personaData,
    conversationFlow,
    mcpServers,
    humanHandoverEnabled,
    completionRules,
    laya,
    llm,
    flowGenerated,
  };

  const buildFlowPayload = () => buildFlowPayloadFor(formState);
  const buildSavePayload = () =>
    buildSavePayloadFor(formState, { buildDataVariables: buildDataVariablesGroups });

  const buildDraftPayload = () => ({ ...(draftIdRef.current ? { id: draftIdRef.current } : {}), ...buildSavePayload() });

  // ------------------------------------------------- autosave (unchanged)
  const autosaveTimer = useRef(null);
  const isFirstRender = useRef(true);
  const dirtyRef = useRef(false);
  const saveChainRef = useRef(Promise.resolve());
  const saveErrorShownRef = useRef(false);
  const latestPayloadRef = useRef(null);
  const [draftState, setDraftState] = useState('idle'); // idle | saving | saved | error

  const autosaveEnabled = !viewOnly && status !== 'published' && !!agentName?.trim();
  const autosaveEnabledRef = useRef(autosaveEnabled);
  autosaveEnabledRef.current = autosaveEnabled;
  latestPayloadRef.current = buildDraftPayload();

  const persistDraft = () => {
    saveChainRef.current = saveChainRef.current.then(async () => {
      if (!dirtyRef.current || !autosaveEnabledRef.current) return;
      dirtyRef.current = false;
      try {
        const currentId = draftIdRef.current;
        const saved = await saveDraft({
          ...latestPayloadRef.current,
          ...(currentId ? { id: currentId, draft_id: currentId } : {}),
        });
        if (!draftIdRef.current && saved?.id) {
          draftIdRef.current = saved.id;
          setDraftId(saved.id);
          const next = new URLSearchParams(window.location.search);
          next.set('draftId', String(saved.id));
          setSearchParams(next, { replace: true });
        }
        saveErrorShownRef.current = false;
        setLastUpdatedAt(new Date().toISOString());
        setDraftState('saved');
      } catch (err) {
        dirtyRef.current = true;
        setDraftState('error');
        console.error('Draft autosave failed', err);
        if (!saveErrorShownRef.current) {
          saveErrorShownRef.current = true;
          toast.error(typeof err === 'string' ? `Draft not saved: ${err}` : 'Draft could not be saved. Please try again.');
        }
      }
    });
    return saveChainRef.current;
  };

  useEffect(() => {
    if (!autosaveEnabled) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dirtyRef.current = true;
    setDraftState('saving');
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(persistDraft, 1000);
    return () => clearTimeout(autosaveTimer.current);
  }, [
    agentName,
    selectedKbId,
    instructions,
    functions,
    followUp,
    dataVariables,
    voiceId,
    language,
    languageId,
    personaType,
    personaData,
    conversationFlow,
    mcpServers,
    humanHandoverEnabled,
    completionRules,
    laya,
    llm,
  ]);

  useEffect(
    () => () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      if (dirtyRef.current) persistDraft();
    },
    [],
  );

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (dirtyRef.current && autosaveEnabledRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ------------------------------------------------- what is still missing
  const filledRules = completionRules.filter((r) => r.type?.trim() || r.description?.trim());
  const hasInstruction = instructions.some((i) => i.enabled !== false && i.body?.trim());
  const personaComplete =
    !!personaType &&
    !!personaData.greeting?.trim() &&
    !!personaData.display_name?.trim() &&
    !!personaData.role_description?.trim();

  const missing = missingForPublish(formState);

  const st = (ok, busy) => (ok ? 'done' : busy ? 'attention' : 'empty');

  const nodes = useMemo(
    () => [
      {
        id: 'basics',
        label: 'Basics',
        children: [
          { id: 'persona', need: 'required', label: 'Persona', status: personaComplete ? 'done' : personaType ? 'attention' : 'empty' },
          { id: 'voice', need: 'required', label: 'Voice', status: voiceId ? 'done' : 'empty' },
          { id: 'languages', need: 'optional', label: 'Languages', status: language ? 'done' : 'empty' },
        ],
      },
      {
        id: 'knows',
        label: 'What it knows and does',
        children: [
          { id: 'instructions', need: 'required', label: 'Instructions', badge: String(instructions.length), status: st(hasInstruction) },
          { id: 'kb', need: 'required', label: 'Knowledge Base', status: st(!!selectedKbId) },
          { id: 'functions', need: 'optional', label: 'Functions', badge: functions.length ? String(functions.length) : '', status: functions.length ? 'done' : 'empty' },
        ],
      },
      {
        id: 'laya',
        label: 'Fast answers',
        // optional while off; once switched on it must be filled in
        need: laya.enabled ? 'required' : 'optional',
        status: laya.enabled ? undefined : 'paused',
        children: [
          ...laya.answers.map((a, i) => ({
            id: `laya-answer-${a.id || i}`,
            label: a.description?.trim() || `Answer ${i + 1}`,
            status: a.enabled === false ? 'paused' : /[<>]/.test(a.answer || '') ? 'attention' : a.answer?.trim() ? 'done' : 'empty',
          })),
          ...(laya.enabled && !laya.answers.some((a) => a.description?.trim() || a.answer?.trim())
            ? [{ id: 'laya-empty', need: 'required', label: 'Add at least one answer', status: 'attention' }]
            : []),
        ],
      },
      {
        id: 'ending',
        label: 'How calls end',
        children: [
          { id: 'completion', need: 'required', label: 'Completion Rules', status: st(filledRules.length > 0) },
          { id: 'handover', need: 'optional', label: 'Human Handover', status: humanHandoverEnabled ? 'done' : 'paused' },
          { id: 'followup', need: 'optional', label: 'Follow-up Rules', status: followUp.steps.some((s) => s.message) ? 'done' : 'empty' },
        ],
      },
      {
        id: 'advanced',
        label: 'Advanced',
        children: [
          { id: 'variables', need: 'optional', label: 'Data Variables', status: dataVariables.length ? 'done' : 'empty' },
          { id: 'flow', need: 'required', label: 'Conversation Flow', status: conversationFlow ? 'done' : 'empty' },
          { id: 'mcp', need: 'optional', label: 'MCP', status: mcpServers.some((s) => s.name) ? 'done' : 'empty' },
        ],
      },
    ],
    [
      personaComplete, personaType, voiceId, language, instructions, hasInstruction, selectedKbId, functions,
      laya, filledRules.length, humanHandoverEnabled, followUp, dataVariables,
      conversationFlow, mcpServers,
    ],
  );

  const summaryLines = [
    `Greets callers as ${personaData.display_name?.trim() || 'your agent'}${voiceId ? '' : ' (no voice picked yet)'}.`,
    hasInstruction ? `Follows ${instructions.filter((i) => i.enabled !== false).length} instruction(s)${selectedKbId ? ' and your knowledge base' : ''}.` : 'Has no instructions yet.',
    laya.enabled && laya.answers.length
      ? `Answers ${laya.answers.filter((a) => a.enabled !== false).length} common question(s) instantly; everything else goes to the AI.`
      : 'Every question goes to the AI — fast answers are off.',
    humanHandoverEnabled ? 'Can transfer the call to a person.' : 'Ends the call instead of transferring to a person.',
  ];

  // ------------------------------------------------- actions
  const handleBack = async () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    await persistDraft();
    navigate(`/${ROUTES.AI_AGENT.AGENTS}`);
  };

  const handlePublish = async () => {
    if (readOnly) {
      setViewOnly(false);
      return;
    }
    if (missing.length) {
      setSelectedId(missing[0].id);
      toast.error(missing[0].label);
      return;
    }
    setSaving(true);
    try {
      dirtyRef.current = false;
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      await saveChainRef.current;
      const result = await saveBot(buildSavePayload());
      const savedAgentId = result?.agent_id || result?.id;
      if (!agentId && savedAgentId) {
        const next = new URLSearchParams(searchParams);
        next.set('id', savedAgentId);
        next.delete('draftId');
        setSearchParams(next, { replace: true });
      }
      setStatus('published');
      toast.success('Agent published');
      setLastUpdatedAt(new Date().toISOString());
      setViewOnly(true);
      navigate(`/${ROUTES.AI_AGENT.AGENTS}`);
    } catch (err) {
      dirtyRef.current = true;
      const message = typeof err === 'string' ? err : 'Failed to save agent';
      toast.error(
        message.includes('duplicate key') || message.includes('idx_agent_config_nm')
          ? 'An agent with this name already exists. Please choose a different name.'
          : message,
      );
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------- detail pane
  const renderDetail = () => {
    const id = selectedId.startsWith('laya-') ? 'laya' : selectedId;
    switch (id) {
      case 'basics':
      case 'persona':
        return (
          <Detail crumbs={['Basics']} title="Persona" help="Who the caller thinks they are talking to, and the first thing they hear.">
            <PersonaTypeSection value={personaType} onChange={setPersonaType} personaData={personaData} onPersonaDataChange={setPersonaData} />
          </Detail>
        );
      case 'voice':
        return (
          <Detail crumbs={['Basics']} title="Voice" help="Pick a voice so callers hear someone. Press play to hear it first.">
            <VoiceIdSection value={voiceId} onChange={setVoiceId} />
          </Detail>
        );
      case 'languages':
        return (
          <Detail crumbs={['Basics']} title="Languages" help="The language the agent speaks and listens in.">
            <LanguagesSection
              value={language}
              onChange={({ id: lid, code }) => {
                setLanguageId(lid);
                setLanguage(code);
              }}
            />
          </Detail>
        );
      case 'instructions':
        return (
          <Detail crumbs={['What it knows and does']} title="Instructions" help="Plain rules the agent follows on every call.">
            <InstructionsCard instructions={instructions} onChange={setInstructions} />
          </Detail>
        );
      case 'kb':
        return (
          <Detail crumbs={['What it knows and does']} title="Knowledge Base" help="The documents the agent answers from.">
            <KnowledgeBaseCard value={selectedKbId} onChange={setSelectedKbId} />
          </Detail>
        );
      case 'functions':
        return (
          <Detail
            crumbs={['What it knows and does']}
            title="Functions"
            help="Let the agent book, look up or update things by calling your API."
          >
            <FunctionsCard value={functions} onChange={setFunctions} />
          </Detail>
        );
      case 'laya':
        return (
          <Detail
            crumbs={['Fast answers', selectedId.startsWith('laya-answer') ? 'Answer' : ''].filter(Boolean)}
            title="Fast answers"
            requiredNow={laya.enabled}
            info="Common questions are answered instantly, without the AI. Anything that does not match an answer is handled by the AI automatically."
            help="Common questions answered instantly, without waiting for the AI."
          >
            <LayaSection value={laya} onChange={setLaya} globallyDisabled={layaGloballyDisabled} readOnly={readOnly} />
          </Detail>
        );
      case 'completion':
        return (
          <Detail crumbs={['How calls end']} title="Completion Rules" help="The conditions that mark a call as finished.">
            <CompletionRulesSection rules={completionRules} onChange={setCompletionRules} humanHandoverEnabled={humanHandoverEnabled} />
          </Detail>
        );
      case 'handover':
        return (
          <Detail crumbs={['How calls end']} title="Human Handover" help="When on, the agent can transfer the call to a live person.">
            <HumanHandoverSection enabled={humanHandoverEnabled} onChange={setHumanHandoverEnabled} />
          </Detail>
        );
      case 'followup':
        return (
          <Detail crumbs={['How calls end']} title="Follow-up Rules" help="What the agent does when the caller goes quiet.">
            <FollowUpRulesSection value={followUp} onChange={setFollowUp} />
          </Detail>
        );
      case 'variables':
        return (
          <Detail crumbs={['Advanced']} title="Data Variables" help="Values the agent collects or receives during a call.">
            <DataVariablesSection value={dataVariables} onChange={setDataVariables} />
          </Detail>
        );
      case 'flow':
        return (
          <Detail crumbs={['Advanced']} title="Conversation Flow" help="A step-by-step outline of how a call should go.">
            <ConversationFlowSection value={conversationFlow} onChange={setConversationFlow} />
          </Detail>
        );
      case 'mcp':
        return (
          <Detail crumbs={['Advanced']} title="MCP" help="Connect external MCP servers so the AI can call outside tools.">
            <McpSection value={mcpServers} onChange={setMcpServers} />
          </Detail>
        );
      default:
        return (
          <Detail crumbs={[]} title={agentName} help="Pick a section on the left to start.">
            <span />
          </Detail>
        );
    }
  };

  // Previous / Next walk the tree in the order it is drawn, so the buttons and
  // the sidebar always agree on "what comes after this".
  const order = useMemo(() => flatten(nodes), [nodes]);

  // "<section> completed" toast once a section turns done, after a short pause
  // so typing the first letter does not announce it. Skips the first render.
  const prevStatuses = useRef(null);
  const doneTimers = useRef({});
  const latestStatuses = useRef({});
  useEffect(() => {
    const now = rollUp(nodes);
    latestStatuses.current = now;
    const prev = prevStatuses.current;
    prevStatuses.current = now;
    if (!prev || readOnly) return;
    flatten(nodes)
      .filter((n) => !n.children?.length && !n.id.startsWith('laya-'))
      .forEach((n) => {
        if (prev[n.id] !== 'done' && now[n.id] === 'done') {
          clearTimeout(doneTimers.current[n.id]);
          doneTimers.current[n.id] = setTimeout(() => {
            if (latestStatuses.current[n.id] === 'done') toast.success(`${n.label} completed`, { id: `done-${n.id}` });
          }, 800);
        }
      });
  }, [nodes, readOnly]);
  useEffect(() => () => Object.values(doneTimers.current).forEach(clearTimeout), []);
  const orderIndex = order.findIndex((n) => n.id === selectedId);
  const prevNode = orderIndex > 0 ? order[orderIndex - 1] : null;
  const nextNode = orderIndex >= 0 ? order[orderIndex + 1] : order[0];

  const detailRef = useRef(null);
  const goTo = (node) => {
    if (!node) return;
    setSelectedId(node.id);
    // a child is useless to land on while its parent is collapsed
    const parent = nodes.find((n) => n.children?.some((c) => c.id === node.id));
    if (parent) setExpanded((e) => ({ ...e, [parent.id]: true }));
    detailRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const statusPill = status === 'published' ? 'Live' : missing.length ? 'Draft' : 'Ready';
  const tree = (
    <BuilderTree
      nodes={nodes}
      selectedId={selectedId}
      onSelect={setSelectedId}
      expanded={expanded}
      onToggle={(id) => setExpanded((e) => ({ ...e, [id]: !e[id] }))}
      className="h-full"
    />
  );

  return (
    <FillPage>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
        <div className="flex min-w-0 items-center gap-2">
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Back to agents" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 lg:hidden" aria-label="Open sections">
                <ListTree className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-3">
              {tree}
            </SheetContent>
          </Sheet>
          <Input
            value={agentName}
            readOnly={readOnly}
            aria-label="Agent name"
            onChange={(e) => setAgentName(e.target.value)}
            className="h-8 w-[clamp(120px,28vw,260px)] border-transparent bg-transparent px-1 text-lg font-semibold shadow-none focus-visible:border-border"
          />
          <Badge variant="secondary" className="hidden sm:inline-flex">{agentTypeLabel}</Badge>
          <Badge variant={statusPill === 'Live' ? 'default' : 'secondary'}>{statusPill}</Badge>
        </div>
        <div className="flex items-center gap-3">
          <span
            aria-live="polite"
            className={
              'flex items-center gap-1 text-xs ' +
              (draftState === 'error' ? 'text-destructive' : draftState === 'saved' ? 'text-[hsl(var(--success))]' : 'text-muted-foreground')
            }
          >
            {draftState === 'saving' && <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>}
            {draftState === 'saved' && <>✓ Saved {lastUpdatedLabel}</>}
            {draftState === 'error' && <>Not saved. Retrying on your next change</>}
            {draftState === 'idle' && <>Last saved {lastUpdatedLabel}</>}
          </span>
          {canImport && (
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
              <FileJson className="h-4 w-4" />
              <span className="hidden sm:inline">Import JSON</span>
            </Button>
          )}
          {!isSuperAdmin && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button size="sm" onClick={handlePublish} disabled={saving || (!readOnly && missing.length > 0)}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      {readOnly ? 'Edit' : saving ? 'Saving…' : 'Publish'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {missing.length > 0 && !readOnly && (
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="mb-1 font-medium">Still missing</p>
                    <ul className="list-disc space-y-0.5 pl-4 text-xs">
                      {missing.map((m) => (
                        <li key={m.id}>{m.label}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* tree · detail · inspector */}
      <FlattenSectionsContext.Provider value>
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[250px_minmax(0,1fr)] xl:grid-cols-[250px_minmax(0,1fr)_300px]">
          <div className="hidden min-h-0 lg:block">{tree}</div>

          <div ref={detailRef} className="min-h-0 overflow-y-auto rounded-2xl border border-border bg-card p-5 lg:p-7">
            {renderDetail()}

            {/* step through the tree without going back to the sidebar */}
            <div className="mx-auto mt-8 flex w-full max-w-3xl items-center justify-between gap-3 border-t border-border pt-4">
              <Button variant="outline" size="sm" disabled={!prevNode} onClick={() => goTo(prevNode)}>
                <ChevronLeft className="h-4 w-4" />
                <span className="truncate">{prevNode ? prevNode.label : 'Previous'}</span>
              </Button>
              {nextNode ? (
                <Button variant="outline" size="sm" onClick={() => goTo(nextNode)}>
                  <span className="truncate">{nextNode.label}</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : readOnly ? (
                <span />
              ) : missing.length > 0 ? (
                // last section: send them to what still blocks publishing
                <Button
                  size="sm"
                  variant="outline"
                  className="border-destructive/40 text-destructive hover:text-destructive"
                  onClick={() => goTo(order.find((n) => n.id === missing[0].id))}
                >
                  <span className="font-bold">!</span>
                  <span className="truncate">Fix {missing.length} missing</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button size="sm" onClick={handlePublish} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  All set, publish
                </Button>
              )}
            </div>
          </div>

          <AgentSummary lines={summaryLines} missing={missing} className="min-h-0 xl:block">
            <FlowchartCard
              buildPayload={buildFlowPayload}
              agentName={agentName}
              instructions={instructions}
              selectedKbId={selectedKbId}
              completionRules={completionRules}
              onFlowGenerated={() => setFlowGenerated(true)}
            />
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-muted/60"
            >
              Jump to…
            </button>
          </AgentSummary>
        </div>
      </FlattenSectionsContext.Provider>

      <ImportJsonDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImport={applyImport}
        options={{ agentType, user, normalizers: { normalizeType: normalizeVariableType, normalizeSource: normalizeVariableSource } }}
      />

      <CommandDialog open={paletteOpen} onOpenChange={setPaletteOpen}>
        <CommandInput placeholder="Jump to a section, or type an action…" />
        <CommandList>
          <CommandEmpty>Nothing matches.</CommandEmpty>
          <CommandGroup heading="Jump to">
            {nodes.flatMap((n) => [n, ...(n.children || [])]).map((n) => (
              <CommandItem
                key={n.id}
                value={n.label}
                onSelect={() => {
                  setSelectedId(n.id);
                  setPaletteOpen(false);
                }}
              >
                {n.label}
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandGroup heading="Actions">
            <CommandItem
              value="add fast answer"
              onSelect={() => {
                setSelectedId('laya');
                setLaya((l) => ({ ...l, enabled: true }));
                setPaletteOpen(false);
              }}
            >
              Add a fast answer
            </CommandItem>
            {canImport && (
              <CommandItem
                value="import agent json"
                onSelect={() => {
                  setImportOpen(true);
                  setPaletteOpen(false);
                }}
              >
                Import agent JSON
              </CommandItem>
            )}
            <CommandItem
              value={laya.enabled ? 'pause fast answers' : 'resume fast answers'}
              onSelect={() => {
                setLaya((l) => ({ ...l, enabled: !l.enabled }));
                setPaletteOpen(false);
              }}
            >
              {laya.enabled ? 'Pause fast answers' : 'Resume fast answers'}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </FillPage>
  );
};

const AgentBuilderV2Page = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initial = useRef({
    draftId: searchParams.get('draftId'),
    agentId: searchParams.get('id'),
    stateAgent: location.state?.agent || null,
  });
  const { draftId: initialDraftId, agentId: initialAgentId, stateAgent } = initial.current;
  const needsFetch = Boolean(initialDraftId || initialAgentId);

  const [ready, setReady] = useState(!needsFetch);
  const [loadedAgent, setLoadedAgent] = useState(needsFetch ? null : stateAgent);

  useEffect(() => {
    if (!needsFetch) return;
    let cancelled = false;
    const user = getUser();
    const request = initialDraftId ? getDraft(initialDraftId) : getAgent(initialAgentId, user?.id);
    request
      .then((fresh) => {
        if (!cancelled) setLoadedAgent(fresh || stateAgent);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadedAgent(stateAgent);
        if (!stateAgent) toast.error(typeof err === 'string' ? err : 'Failed to load agent');
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) {
    return (
      <FillPage>
        <PageLoader label="Loading agent…" />
      </FillPage>
    );
  }
  return <AgentBuilderEditor loadedAgent={loadedAgent} />;
};

export default AgentBuilderV2Page;
